/**
 * @file SyncContacts.js
 * @version 1.2.5
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-07-07
 * @modified 2026-07-08
 * @description Map Epos Now customers to local (Xero-synced) accounts. Lists Epos
 *              customers by match status (Not matched / Matched / All), shows their
 *              address and trigram-ranked suggestions, and lets the user confirm or
 *              adjust a match, create a new contact prefilled with the Epos data
 *              (which syncs to Xero), view the selected account, or ignore a defunct
 *              Epos customer. The confirmed mapping (epos_now.customers.account_id)
 *              is what Till Sales reads before allowing invoice creation — unmatched
 *              Epos customers are blocked. Paginated (client-side) for the larger views.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './TableStyles.css';
import './SyncContacts.css';
import Input from './Input';
import Dropdown from './Dropdown';
import LoadingSpinner from './LoadingSpinner';
import SearchableDropdown from './SearchableDropdown';
import PaginationControls from './PaginationControls';
import ContactEditModal from './ContactEditModal';
import ContactModal from './ContactModal';
import { showAlert } from './Alert';
import ConfirmDialog from './ConfirmDialog';
import { useSlideOutButton } from './SlideOutButton';

const PAGE_SIZE = 25;
const STATUS_OPTIONS = [
  { value: 'unmatched', label: 'Not matched' },
  { value: 'matched', label: 'Matched' },
  { value: 'all', label: 'All' }
];

function SyncContacts() {
  const [customers, setCustomers] = useState([]);
  const [accounts, setAccounts] = useState([]); // all active local accounts (dropdown options)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('unmatched');
  const [page, setPage] = useState(1);

  // Per-row chosen account id, keyed by eposCustomerId. Defaults to the row's
  // current mapping (if any), else its top suggestion, so a single click on
  // Confirm does the right thing.
  const [chosen, setChosen] = useState({});
  const [busyId, setBusyId] = useState(null); // eposCustomerId currently resolving/ignoring
  const [createFor, setCreateFor] = useState(null); // Epos customer being turned into a new contact
  const [viewContactId, setViewContactId] = useState(null); // account shown in the ContactModal
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });

  useSlideOutButton([refreshing, loading]);

  const loadAccounts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/contacts?limit=10000`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAccounts(data.contacts || []);
    } catch (e) {
      console.error('Accounts fetch error:', e);
    }
  };

  const loadCustomers = async (status = statusFilter) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/epos-now/contacts/unmatched?status=${status}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = data.customers || [];
      setCustomers(list);
      // Seed each row's chosen account with its current mapping, else its best
      // suggestion.
      const init = {};
      for (const c of list) {
        init[c.eposCustomerId] = c.matchedAccountId || c.suggestions?.[0]?.id || null;
      }
      setChosen(init);
    } catch (e) {
      console.error('Customers fetch error:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccounts();
  }, []);

  // Reload the list whenever the status filter changes; reset to page 1.
  useEffect(() => {
    setPage(1);
    loadCustomers(statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Any new search collapses back to the first page of results.
  useEffect(() => { setPage(1); }, [search]);

  // "line1, postcode" — the at-a-glance address for comparing candidates.
  const addrLine = (line1, postcode) => [line1, postcode].filter(Boolean).join(', ');

  const accountOptions = useMemo(() => (
    accounts.map(a => {
      const addr = addrLine(a.address_line1, a.address_postcode);
      return {
        value: a.id,
        badge: a.account_no || '',
        // Phone parts included so the dropdown finds accounts by phone/mobile —
        // SearchableDropdown matches digits-only, so formatting doesn't matter.
        searchText: `${a.company_name} ${a.account_no || ''} ${a.email || ''} ${addr} ${[a.phone_country_code, a.phone_area_code, a.phone, a.mobile_country_code, a.mobile_area_code, a.mobile].filter(Boolean).join(' ')}`,
        renderLabel: (
          <span className="ec-opt">
            <span className="ec-opt-main">
              <span className="ec-opt-name">{a.company_name}</span>
            </span>
            {addr && <span className="ec-opt-addr">{addr}</span>}
          </span>
        )
      };
    })
  ), [accounts]);

  // Colour band for a similarity score: exact=green, close=amber, weak=red.
  const pctClass = (score) => {
    const pct = Math.round((score || 0) * 100);
    if (pct >= 100) return 'pct-full';
    if (pct >= 66) return 'pct-mid';
    return 'pct-low';
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(c => {
      const hay = `${c.fullName || ''} ${c.businessName || ''} ${c.email || ''} ${c.contactNumber || ''} ` +
        `${c.addressLine1 || ''} ${c.town || ''} ${c.postcode || ''} ` +
        `${c.matchedCompanyName || ''} ${c.matchedAccountNo || ''}`.toLowerCase();
      return hay.toLowerCase().includes(q);
    });
  }, [customers, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = useMemo(
    () => filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filtered, safePage]
  );
  const pagination = { page: safePage, limit: PAGE_SIZE, total: filtered.length, totalPages };

  const removeRow = (eposCustomerId) => {
    setCustomers(prev => prev.filter(c => c.eposCustomerId !== eposCustomerId));
  };

  // Update a row's mapping in place (used in the matched/all views where the row
  // stays visible after a re-map rather than disappearing).
  const updateRowMapping = (eposCustomerId, accountId) => {
    const acc = accounts.find(a => a.id === accountId);
    setCustomers(prev => prev.map(c => c.eposCustomerId === eposCustomerId ? {
      ...c,
      matchStatus: 'user_confirmed',
      matchedAccountId: accountId,
      matchedCompanyName: acc?.company_name || c.matchedCompanyName,
      matchedAccountNo: acc?.account_no || c.matchedAccountNo
    } : c));
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/epos-now/contacts/auto-match`, {
        method: 'POST', credentials: 'include'
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      await loadAccounts();
      await loadCustomers();
      const synced = data.synced || {};
      showAlert(
        `Refreshed from Epos Now: ${synced.added || 0} new, ${synced.updated || 0} updated. ` +
        `${data.autoMatched || 0} exact match${data.autoMatched === 1 ? '' : 'es'} auto-confirmed.`,
        'success'
      );
    } catch (e) {
      console.error('Auto-match error:', e);
      showAlert(`Refresh failed: ${e.message}`, 'error');
    } finally {
      setRefreshing(false);
    }
  };

  const confirmMatch = async (customer, accountId) => {
    if (!accountId) return;
    setBusyId(customer.eposCustomerId);
    try {
      const res = await fetch(`${API_URL}/api/epos-now/contacts/resolve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eposCustomerId: customer.eposCustomerId, accountId })
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || `HTTP ${res.status}`);
      }
      // In the "Not matched" view a confirmed row leaves the list; in the
      // matched/all views it stays and just reflects the new mapping.
      if (statusFilter === 'unmatched') removeRow(customer.eposCustomerId);
      else updateRowMapping(customer.eposCustomerId, accountId);
    } catch (e) {
      showAlert(`Could not confirm match: ${e.message}`, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const askIgnore = (customer) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Ignore Epos customer',
      message: `Ignore "${customer.businessName || customer.fullName}"? Their till sales will stay blocked from invoicing until you map them. Use this for defunct or duplicate Epos customers.`,
      onConfirm: () => { closeConfirm(); ignoreCustomer(customer); }
    });
  };

  const ignoreCustomer = async (customer) => {
    setBusyId(customer.eposCustomerId);
    try {
      const res = await fetch(`${API_URL}/api/epos-now/contacts/ignore`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eposCustomerId: customer.eposCustomerId })
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || `HTTP ${res.status}`);
      }
      removeRow(customer.eposCustomerId);
    } catch (e) {
      showAlert(`Could not ignore customer: ${e.message}`, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const closeConfirm = () => setConfirmDialog({ isOpen: false, message: '', onConfirm: null });

  // ContactEditModal saved a freshly-created contact — link it to the Epos
  // customer that opened the modal, then drop the row.
  const handleContactCreated = async (createdInXero, contact) => {
    const customer = createFor;
    setCreateFor(null);
    if (!customer || !contact?.id) {
      loadCustomers();
      return;
    }
    await confirmMatch(customer, contact.id);
    if (!createdInXero) {
      showAlert('Contact created locally (not synced to Xero). Check Xero connection.', 'warning');
    }
  };

  // Synthetic contact prop for ContactEditModal — no id ⇒ create mode, but the
  // form still prefills from these fields (see ContactEditModal prefill effect).
  const prefillContact = useMemo(() => {
    if (!createFor) return null;
    // Single Epos address → prefill both invoice and delivery (ContactEditModal's
    // prefill effect reads these DB-style delivery_*/invoice_* field names).
    const line1 = createFor.addressLine1 || '';
    const line2 = createFor.addressLine2 || '';
    const city = createFor.town || '';
    const region = createFor.county || '';
    const postcode = createFor.postcode || '';
    return {
      company_name: createFor.businessName || createFor.fullName || '',
      email: createFor.email || '',
      phone: createFor.contactNumber || '',
      invoice_line1: line1, invoice_line2: line2, invoice_city: city, invoice_region: region, invoice_postcode: postcode,
      delivery_line1: line1, delivery_line2: line2, delivery_city: city, delivery_region: region, delivery_postcode: postcode,
      notes: `Imported from Epos Now (customer #${createFor.eposCustomerId})`
    };
  }, [createFor]);

  const formatBalance = (n) => {
    const v = Number(n) || 0;
    return `£${v.toFixed(2)}`;
  };

  return (
    <div className="sync-contacts">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">
            Map Epos Now customers to accounts. Till sales for an unmapped Epos customer are blocked. Use the
            filter to review or adjust already-matched customers.
          </span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>
              {filtered.length} {statusFilter === 'unmatched' ? 'unmatched' : statusFilter === 'matched' ? 'matched' : ''} customer{filtered.length !== 1 ? 's' : ''}
              {search ? ' (filtered)' : ''}
            </span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button className="btn-slideout" onClick={handleRefresh} disabled={loading || refreshing}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">cloud_sync</span></div>
                <span className="btn-label">{refreshing ? 'Refreshing…' : 'Refresh from Epos'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters">
        <div className="filter-row">
          <div className="filter-group" style={{ flex: 2 }}>
            <label>Search</label>
            <Input
              type="text"
              placeholder="Name, email, phone, address or matched account"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon="search"
            />
          </div>
          <div className="filter-group" style={{ minWidth: '170px' }}>
            <label>Match status</label>
            <Dropdown
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={(val) => setStatusFilter(val)}
            />
          </div>
        </div>
      </div>

      {!loading && !error && totalPages > 1 && (
        <div className="sync-contacts-pagination">
          <PaginationControls pagination={pagination} onPageChange={setPage} />
        </div>
      )}

      <div className="sync-contacts-panel">
        <div className="data-table-container">
          {loading ? (
            <div style={{ padding: 'var(--spacing-xl)', textAlign: 'center' }}><LoadingSpinner /></div>
          ) : error ? (
            <div className="sync-contacts-error">
              <span className="material-symbols-rounded">error</span>
              <span>{error}</span>
            </div>
          ) : (
            <table className="data-table sync-contacts-table">
              <thead>
                <tr>
                  <th>Epos customer</th>
                  <th className="text-right">Balance</th>
                  <th style={{ width: '280px' }}>Suggestions</th>
                  <th style={{ width: '320px' }}>Match to account</th>
                  <th style={{ width: '150px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ padding: '30px', textAlign: 'center', color: 'var(--text-muted)' }}>
                      {customers.length > 0
                        ? 'No customers match the current search.'
                        : statusFilter === 'unmatched'
                          ? 'Every Epos customer is mapped — nothing to resolve.'
                          : 'No customers in this view.'}
                    </td>
                  </tr>
                ) : paged.map(c => {
                  const busy = busyId === c.eposCustomerId;
                  const chosenId = chosen[c.eposCustomerId] || null;
                  const suggestionOptions = (c.suggestions || []).map(s => {
                    const pct = Math.round((s.score || 0) * 100);
                    const addr = addrLine(s.line1, s.postcode);
                    return {
                      value: s.id,
                      badge: s.accountNo || '',
                      searchText: `${s.companyName} ${s.accountNo || ''} ${addr}`,
                      renderLabel: (
                        <span className="ec-opt">
                          <span className="ec-opt-main">
                            <span className="ec-opt-name">{s.companyName}</span>
                            <span className={`ec-opt-pct ${pctClass(s.score)}`}>{pct}%</span>
                          </span>
                          {addr && <span className="ec-opt-addr">{addr}</span>}
                        </span>
                      )
                    };
                  });
                  return (
                    <tr key={c.eposCustomerId} className={busy ? 'row-busy' : ''}>
                      <td>
                        <div className="ec-name-cell">
                          <span className="ec-name">{c.businessName || c.fullName || '(no name)'}</span>
                          {c.businessName && c.fullName && c.businessName !== c.fullName && (
                            <span className="ec-sub">{c.fullName}</span>
                          )}
                          {c.matchStatus && c.matchStatus !== 'unmatched' && (
                            <span className={`ec-status-pill ${c.matchStatus === 'user_confirmed' ? 'confirmed' : 'auto'}`}>
                              {c.matchStatus === 'user_confirmed' ? 'Confirmed' : 'Auto'}
                            </span>
                          )}
                        </div>
                        <div className="ec-meta">
                          EPOS-{c.eposCustomerId}
                          {c.email ? ` · ${c.email}` : ''}
                          {c.contactNumber ? ` · ${c.contactNumber}` : ''}
                        </div>
                        {(c.addressLine1 || c.town || c.postcode) && (
                          <div className="ec-addr">
                            <span className="material-symbols-rounded">location_on</span>
                            <span>{[c.addressLine1, c.town, c.postcode].filter(Boolean).join(', ')}</span>
                          </div>
                        )}
                      </td>
                      <td className="text-right">{formatBalance(c.currentBalance)}</td>
                      <td>
                        {suggestionOptions.length === 0 ? (
                          <span style={{ color: 'var(--text-muted)' }}>—</span>
                        ) : (
                          <SearchableDropdown
                            options={suggestionOptions}
                            value={chosenId}
                            onChange={(v) => setChosen(prev => ({ ...prev, [c.eposCustomerId]: v }))}
                            placeholder="Pick a suggestion…"
                            searchPlaceholder="Search suggestions…"
                            disabled={busy}
                          />
                        )}
                      </td>
                      <td>
                        <SearchableDropdown
                          options={accountOptions}
                          value={chosenId}
                          onChange={(v) => setChosen(prev => ({ ...prev, [c.eposCustomerId]: v }))}
                          placeholder="Select account…"
                          searchPlaceholder="Search accounts…"
                          disabled={busy}
                        />
                      </td>
                      <td>
                        <div className="actions-cell">
                          <button
                            className="btn-icon"
                            title={c.matchStatus && c.matchStatus !== 'unmatched' ? 'Update match' : 'Confirm match'}
                            disabled={busy || !chosenId || (chosenId === c.matchedAccountId && c.matchStatus === 'user_confirmed')}
                            onClick={() => confirmMatch(c, chosenId)}
                          >
                            <span className="material-symbols-rounded">check</span>
                          </button>
                          <button
                            className="btn-icon"
                            title="View selected account"
                            disabled={busy || !chosenId}
                            onClick={() => setViewContactId(chosenId)}
                          >
                            <span className="material-symbols-rounded">contact_page</span>
                          </button>
                          <button
                            className="btn-icon"
                            title="Create new contact from Epos data"
                            disabled={busy}
                            onClick={() => setCreateFor(c)}
                          >
                            <span className="material-symbols-rounded">person_add</span>
                          </button>
                          <button
                            className="btn-icon"
                            title="Ignore (defunct / duplicate Epos customer)"
                            disabled={busy}
                            onClick={() => askIgnore(c)}
                          >
                            <span className="material-symbols-rounded">block</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {createFor && (
        <ContactEditModal
          contact={prefillContact}
          onClose={() => setCreateFor(null)}
          onSave={handleContactCreated}
        />
      )}

      {viewContactId && (
        <ContactModal contactId={viewContactId} onClose={() => setViewContactId(null)} />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Ignore"
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
}

export default SyncContacts;
